import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UserList from './UserList';
import SignatureInputForm from './SignatureInputForm';
import ApiKeyInput from './ApiKeyInput';
import { fetchAdminUsers, applySignaturesBatch, suspendUser } from '../../services/adminApi';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

// Les fonctions API sont maintenant importées depuis adminApi.js
// const fetchAdminUsers = async (apiKey) => { ... };
// const applySignaturesBatch = async (apiKey, userEmails, signature) => { ... };

function AdminDashboard() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('adminApiKey') || '');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [signature, setSignature] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // État pour la configuration du tri : { key: string, direction: 'ascending' | 'descending' }
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  const handleApiKeyChange = (key) => {
    setApiKey(key);
    localStorage.setItem('adminApiKey', key);
    setIsAuthenticated(false);
    setUsers([]);
    setError(null);
    setBatchResult(null);
    setSortConfig({ key: 'name', direction: 'ascending' }); // Réinitialiser le tri
  };

  const validateAndLoadUsers = useCallback(async () => {
    if (!apiKey) {
      setError('Veuillez fournir une clé API pour charger les utilisateurs.');
      setIsAuthenticated(false);
      setUsers([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    setBatchResult(null);
    try {
      const data = await fetchAdminUsers(apiKey);
      // Les données du backend sont déjà triées par creationTime desc.
      // Le tri côté client s'appliquera par-dessus ou si l'utilisateur change le tri.
      setUsers(data.users || []);
      setIsAuthenticated(true);
    } catch (err) {
      setError(err.message);
      setUsers([]);
      setIsAuthenticated(false);
    }
    setIsLoading(false);
  }, [apiKey]);

  useEffect(() => {
    const storedApiKey = localStorage.getItem('adminApiKey');
    if (storedApiKey) {
      // Pas besoin d'appeler setApiKey ici car l'état initial le fait déjà.
      // On appelle directement validateAndLoadUsers si on veut une auto-connexion.
      // Pour l'instant, on laisse l'utilisateur cliquer sur "Charger les utilisateurs".
      // Si vous voulez une tentative de connexion auto au démarrage si la clé existe:
      // validateAndLoadUsers(); 
      // Cependant, il est peut-être préférable d'attendre une action explicite de l'utilisateur.
    }
  }, []);

  const handleUserSelectionChange = (userId, isSelected) => {
    setSelectedUsers(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (isSelected) {
        newSelected.add(userId);
      } else {
        newSelected.delete(userId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (areAllSelected) => {
    if (areAllSelected) {
        setSelectedUsers(new Set());
    } else {
        setSelectedUsers(new Set(users.map(u => u.email)));
    }
  };

  const handleApplySignatures = async () => {
    if (selectedUsers.size === 0) {
      setError('Veuillez sélectionner au moins un utilisateur.');
      return;
    }
    if (!signature) {
      setError('Veuillez entrer une signature.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setBatchResult(null);
    try {
      const result = await applySignaturesBatch(apiKey, Array.from(selectedUsers), signature);
      setBatchResult(result);
      // Rafraîchir la liste des utilisateurs pour voir les changements de statut
      // et potentiellement l'état de suspension après une action de lot (si pertinent à l'avenir)
      await validateAndLoadUsers(); 
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const handleSuspendUser = async (userEmail) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Êtes-vous sûr de vouloir suspendre l'utilisateur ${userEmail} ? Cette action est réversible via la console Google Admin.`)) {
      setIsLoading(true);
      setError(null);
      try {
        await suspendUser(apiKey, userEmail);
        // Rafraîchir la liste des utilisateurs pour voir le statut de suspension mis à jour
        await validateAndLoadUsers(); 
        // Afficher un message de succès temporaire si nécessaire
        // setSuccessMessage(`Utilisateur ${userEmail} suspendu avec succès.`);
      } catch (err) {
        setError(err.message);
      }
      setIsLoading(false);
    }
  };

  // Fonction pour demander un tri
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Nouvelle fonction pour utiliser une signature stockée
  const handleUseStoredSignature = (storedSignatureHtml) => {
    setSignature(storedSignatureHtml);
    // Optionnel : faire défiler l'utilisateur vers le champ de signature ou afficher une notification
    const signatureTextarea = document.getElementById('signatureTextarea');
    if (signatureTextarea) {
      signatureTextarea.focus();
      signatureTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setError(null); // Effacer les erreurs précédentes si l'utilisateur choisit une signature
  };

  // Trier les utilisateurs avant de les passer à UserList
  const sortedUsers = useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Cas spécifiques pour certains champs
        if (sortConfig.key === 'statusDetails') { 
          aValue = a.statusDetails?.status || '';
          bValue = b.statusDetails?.status || '';
        }
        
        if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
          // false (Non) est "plus petit" que true (Oui)
          if (aValue === bValue) return 0;
          if (sortConfig.direction === 'ascending') {
            return aValue ? 1 : -1; // false (-1) vient avant true (1)
          } else { // descending
            return aValue ? -1 : 1; // true (-1) vient avant false (1)
          }
        }

        // Comparaison standard pour chaînes et nombres
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  return (
    <div className="container mx-auto max-w-full bg-white p-6 md:p-8 rounded-lg shadow-lg mt-4 mb-8">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">Tableau de bord d'administration</h2>
      
      <ApiKeyInput apiKey={apiKey} onApiKeyChange={handleApiKeyChange} onLoadUsers={validateAndLoadUsers} isLoading={isLoading} />

      {error && (
        <div className="my-4 p-4 bg-red-50 border border-red-300 text-red-700 rounded-md flex items-center">
          <AlertCircle size={20} className="mr-3" />
          <span>Erreur: {error}</span>
        </div>
      )}
      {isLoading && (
        <div className="my-4 p-4 bg-blue-50 border border-blue-300 text-blue-700 rounded-md flex items-center">
          <Loader2 size={20} className="mr-3 animate-spin" />
          <span>Chargement...</span>
        </div>
      )}

      {isAuthenticated && (
        <>
          <SignatureInputForm signature={signature} onSignatureChange={setSignature} />
          <button 
            onClick={handleApplySignatures} 
            disabled={isLoading || selectedUsers.size === 0 || !signature}
            className="mt-4 w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Appliquer la signature aux {selectedUsers.size} utilisateurs sélectionnés
          </button>
          <UserList 
            users={sortedUsers} // Passer les utilisateurs triés
            selectedUsers={selectedUsers} 
            onUserSelectionChange={handleUserSelectionChange}
            onSelectAll={handleSelectAll}
            requestSort={requestSort} // Passer la fonction de demande de tri
            sortConfig={sortConfig} // Passer la configuration de tri actuelle
            onUseStoredSignature={handleUseStoredSignature} // Passer la nouvelle fonction
            onSuspendUser={handleSuspendUser} // Passer la fonction de suspension
          />
        </>
      )}
      {!isAuthenticated && apiKey && !isLoading && error && (
         <div className="my-4 p-4 bg-yellow-50 border border-yellow-300 text-yellow-700 rounded-md flex items-center">
            <AlertCircle size={20} className="mr-3" />
            <span>La clé API semble invalide. Vérifiez la clé et cliquez sur "Charger les utilisateurs".</span>
        </div>
      )}
      {!isAuthenticated && apiKey && !isLoading && !error && (
         <div className="my-4 p-4 bg-blue-50 border border-blue-300 text-blue-700 rounded-md flex items-center">
            <CheckCircle size={20} className="mr-3" />
            <span>Clé API fournie. Cliquez sur "Charger les utilisateurs" pour valider et afficher les données.</span>
        </div>
      )}
      
      {batchResult && (
        <div className={`mt-6 p-4 border rounded-md ${batchResult.details && batchResult.details.some(d => !d.success) ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
          <h4 className={`text-lg font-semibold ${batchResult.details && batchResult.details.some(d => !d.success) ? 'text-yellow-800' : 'text-green-800'}`}>Résultat du traitement par lot :</h4>
          <p className={`mt-1 mb-2 ${batchResult.details && batchResult.details.some(d => !d.success) ? 'text-yellow-700' : 'text-green-700'}`}>{batchResult.message}</p>
          {batchResult.details && batchResult.details.length > 0 && (
            <ul className="max-h-48 overflow-y-auto list-disc list-inside space-y-1 text-sm">
              {batchResult.details.map(item => (
                <li key={item.email} className={item.success ? 'text-green-600' : 'text-red-600'}>
                  <span className="font-medium">{item.email}:</span> {item.status} - {item.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard; 